import { useState } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts, type BlogPost } from '../../../mocks/blog';

const ALL_CATEGORIES = 'Všetky';

const categories = [
  ALL_CATEGORIES,
  ...Array.from(new Set(blogPosts.map((p) => p.category))),
];

export default function BlogPageGrid() {
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORIES);

  const filtered =
    activeCategory === ALL_CATEGORIES
      ? blogPosts
      : blogPosts.filter((p) => p.category === activeCategory);

  const [featured, ...rest] = filtered;

  return (
    <section className="bg-zinc-950 pb-28">
      <div className="max-w-7xl mx-auto px-6">
        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-orange-500 text-white'
                  : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Featured article */}
        {featured && (
          <Link
            to={`/blog/${featured.id}`}
            className="group block mb-10 bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all duration-300 cursor-pointer"
          >
            <div className="grid md:grid-cols-2">
              <div className="aspect-video md:aspect-auto md:min-h-[340px] overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="flex flex-col justify-between p-8 lg:p-10">
                <div>
                  <div className="flex items-center gap-3 mb-5">
                    <span className={`text-xs font-semibold rounded-full px-3 py-1 ${featured.categoryColor}`}>
                      {featured.category}
                    </span>
                    <span className="bg-orange-500/10 text-orange-400 text-xs font-semibold rounded-full px-3 py-1 border border-orange-500/20">
                      Odporúčané
                    </span>
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-black text-white leading-snug mb-4 group-hover:text-orange-300 transition-colors duration-200">
                    {featured.title}
                  </h2>
                  <p className="text-zinc-400 text-base leading-relaxed mb-6">
                    {featured.excerpt}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden border border-zinc-700">
                      <img src={featured.authorImage} alt={featured.author} className="w-full h-full object-cover object-top" />
                    </div>
                    <div>
                      <p className="text-white text-xs font-semibold">{featured.author}</p>
                      <p className="text-zinc-500 text-[10px]">{featured.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
                    <i className="ri-time-line" />
                    <span>{featured.readTime}</span>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Grid */}
        {rest.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        )}

        {filtered.length === 0 && (
          <div className="text-center py-24 text-zinc-600">
            <i className="ri-file-search-line text-4xl mb-3" />
            <p className="text-lg">Žiadne články v tejto kategórii</p>
          </div>
        )}
      </div>
    </section>
  );
}

function BlogCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blog/${post.id}`}
      className="group flex flex-col bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
    >
      <div className="aspect-video overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
      </div>
      <div className="flex flex-col flex-1 p-6">
        <div className="flex items-center justify-between mb-4">
          <span className={`text-xs font-semibold rounded-full px-3 py-1 ${post.categoryColor}`}>
            {post.category}
          </span>
          <span className="text-zinc-600 text-xs flex items-center gap-1">
            <i className="ri-time-line" />
            {post.readTime}
          </span>
        </div>
        <h3 className="text-white font-bold text-base leading-snug mb-3 group-hover:text-orange-300 transition-colors duration-200 flex-1">
          {post.title}
        </h3>
        <p className="text-zinc-500 text-sm leading-relaxed mb-5 line-clamp-2">
          {post.excerpt}
        </p>
        <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full overflow-hidden border border-zinc-700">
              <img src={post.authorImage} alt={post.author} className="w-full h-full object-cover object-top" />
            </div>
            <span className="text-zinc-400 text-xs font-medium">{post.author}</span>
          </div>
          <span className="text-zinc-600 text-xs">{post.date}</span>
        </div>
      </div>
    </Link>
  );
}
